import React, { useEffect, useRef, useState } from "react";
import { Input, Table, message, Spin, Tag, Space, Button, Modal, ConfigProvider, Tabs } from "antd";
import Column from "antd/es/table/Column";
import { isNotNullUndefined, UseDebounce } from "../helpers/common";
import { useChangeAgent, useGetAvailableAttendants, useUpdateTripPreferences } from "../services/trip.service";
import Pagination from "../helpers/pagination";
import { storage } from "../utils/storage";
import { useNavigate, useLocation } from "react-router-dom";
import { CloseOutlined } from "@ant-design/icons";
import { AgentData, NavigationState } from "../types/trip";

const AvailableAttendants: React.FC = () => {
    const { Search } = Input;
    const { TabPane } = Tabs;
    const navigate = useNavigate();
    const { state } = useLocation();
    const { tripId, scheduleIds, referenceId, } = state as NavigationState
    const [agentListData, setAgentList] = useState<AgentData[]>([]);
    const pageNumber = useRef(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [modalType, setModalType] = useState<'change' | ''>('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [formData, setFormData] = useState<any>({
        tripId: tripId,
        referenceId: referenceId,
        newAgentId: ''
    });
    const debouncedQuery = UseDebounce(searchQuery, 500);

    const [tripPreferences, setTripPreferences] = useState<any[]>([
        {
            preferenceName: "Transportation",
            preferenceValue: "true"
        },
        {
            preferenceName: "Gender",
            preferenceValue: ""
        },
        {
            preferenceName: "Languages",
            preferenceValue: ""
        }
    ]);
    const tabFilterType = useRef<boolean | null>(true);

    const tabItems: any = [
        {
            key: 1,
            name: "Attendants without Car",
            value: true
        },
        {
            key: 2,
            name: "Attendants with Car",
            value: false
        }
    ]

    useEffect(() => {
        let userRoleId = parseInt(storage.getUserRoleId() ?? "0");
        if (userRoleId !== 1) {
            navigate("/");
        }
        else {
            updateTripPreferencesMutation.mutate({ tripId: tripId, scheduleIds: scheduleIds, isAdmin: true, tripPreferences });
        }
    }, [tabFilterType.current]);

    useEffect(() => {
        fetchAgentList();
    }, [debouncedQuery]);

    const fetchAgentList = async () => {
        setIsLoading(true);
        setAgentList([]);
        getAvailableAttendantsMutation.mutate({ tripId: tripId, referenceId: referenceId, pageNumber: pageNumber.current, pageSize: pageSize, searchFilter: searchQuery });
    };

    const getAvailableAttendantsMutation = useGetAvailableAttendants({
        onSuccess: (response: any) => {
            if (response.statusCode === 200) {
                if (isNotNullUndefined(response.data)) {
                    const agentAvailability = response.data.agentAvailability || [];

                    if (agentAvailability.length > 0) {
                        const formattedData = agentAvailability.map((agent: AgentData) => ({
                            key: agent.agentId,
                            agentId: agent.agentId,
                            agentName: agent.agentName,
                            agentBio: agent.agentBio,
                            agentImageUrl: agent.agentImageUrl,
                            agentCityName: agent.agentCityName,
                            hourlyRate: agent.hourlyRate,
                            agentRating: agent.agentRating,
                            scheduleId: agent.scheduleId,
                            phoneNo: agent.phoneNo,
                        }));
                        setAgentList(formattedData);
                        setTotalPages(Math.ceil(agentAvailability.recordCount / pageSize));
                    } else {
                        setAgentList([]);
                    }
                    setIsLoading(false);
                } else {
                    setAgentList([]);
                    setIsLoading(false);
                }
            } else {
                message.error(response.statusMessage || "Failed to fetch agents");
                setIsLoading(false);
            }
        },
        onError: (error: any) => {
            message.error(error.message || "An error occurred");
            setIsLoading(false);
        }
    });

    const handlePageChange = (newPage: number) => {
        pageNumber.current = newPage;
        setPageSize(10);
        fetchAgentList();
    };

    const handleChangeAgent = (data: any) => {
        setFormData({
            ...formData,
            newAgentId: data.agentId
        })
        setIsModalVisible(true);
        setModalType('change');
    }


    const ChangeAgentMutation = useChangeAgent({
        onSuccess: (res: any) => {
            if (res.statusCode === 200) {
                message.success(res.statusMessage || "Agent changed successfully");
                navigate(`/trips/${tripId}/${scheduleIds}`);
                setIsModalVisible(false);
                setIsLoading(false);
            }
            else {
                setIsModalVisible(false);
                setIsLoading(false);
                message.error(res.statusMessage || "Agent changed successfully");
            }

        },
        onError: (err) => {
            message.error(err.message || "An error occurred");
            setIsModalVisible(false);
            setIsLoading(false);
        }
    });

    const getModelContent = () => {
        let modalContent = <></>;
        if (modalType === 'change') {
            modalContent = (
                <>
                    <p className="mb-4">
                        Are you sure you want to change Trip's Attendant?
                    </p>
                </>
            );
        }

        return modalContent;
    };

    const getModalTitle = () => {
        let message = "";
        if (modalType === 'change') {
            message = "Confirm Change!";
        }
        return message;
    };


    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleSave = () => {
        setIsLoading(true);
        ChangeAgentMutation.mutate(formData);
    };

    const tabClicked = (key: number) => {
        tabFilterType.current = tabItems.find((x: any) => x.key === key)?.value ?? null;
        pageNumber.current = 1;
        setTripPreferences((prevPreferences) => {
            return prevPreferences.map((preference) => {
                if (preference.preferenceName === "Transportation") {
                    return {
                        ...preference,
                        preferenceValue: tabFilterType.current ? "true" : "false"
                    };
                }
                return preference;
            });
        });
    };

    const updateTripPreferencesMutation = useUpdateTripPreferences({
        onSuccess: (res: any) => {
            if (res.statusCode === 200) {
                message.success(res.statusMessage || "Trip preferences updated successfully");
                fetchAgentList();
            }
        },
        onError: (err) => {
            message.error(err.message || "An error occurred");
        }
    })

    return (
        <div>
            <div className="flex justify-between align-middle pb-5">
                <h2 className="text-3xl font-semibold text-black">Available Attendants</h2>
                <div className="flex justify-between gap-5">
                    <div className="w-80 max-w-80">
                        <Search
                            placeholder="Search attendant"
                            size="large"
                            enterButton
                            className="search-field"
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <ConfigProvider
                theme={{
                    components: {
                        Tabs: {
                            titleFontSizeLG: 16,
                            horizontalItemPadding: '8px 0',
                            inkBarColor: '#000',
                        },
                    },
                }
                }
            >
                <Tabs defaultActiveKey="1" onTabClick={(e) => { tabClicked(parseInt(e)) }}>
                    <TabPane tab="Attendants without Car" key="1">
                        <Table
                            dataSource={agentListData}
                            className="userlist-table cursor-pointer"
                            bordered
                            pagination={{ pageSize: pageSize, position: ['none'] }}
                            locale={{
                                emptyText: loading ? <Spin /> : (
                                    <div className="ant-empty">
                                        <div className="ant-empty-image">
                                            <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
                                                <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                                                    <ellipse fill="#f5f5f5" cx="32" cy="33" rx="32" ry="7"></ellipse>
                                                    <g fillRule="nonzero" stroke="#d9d9d9">
                                                        <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"></path>
                                                        <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" fill="#fafafa"></path>
                                                    </g>
                                                </g>
                                            </svg>
                                        </div>
                                        <div className="ant-empty-description">There are no attendant available for the date and time selected</div>
                                    </div>
                                )
                            }}
                        >
                            <Column title="Attendant Name" dataIndex="agentName" key="agentName" align="center" width={200} />
                            <Column title="Bio" dataIndex="agentBio" key="agentBio" align="center" width={250}
                                render={(bio: string) => bio || 'N/A'} />
                            <Column title="Image" dataIndex="agentImageUrl" key="agentImageUrl" align="center" width={100}
                                render={(imageUrl: string) =>
                                    imageUrl ? (
                                        <img src={imageUrl} alt="agent" className="w-10 h-10 rounded-full mx-auto object-cover" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-full mx-auto bg-gray-200 flex items-center justify-center">
                                            N/A
                                        </div>
                                    )
                                } />
                            <Column title="Phone No" dataIndex="phoneNo" key="phoneNo" align="center" width={150} />
                            <Column title="City" dataIndex="agentCityName" key="agentCityName" align="center" width={150} />
                            <Column title="Hourly Rate" dataIndex="hourlyRate" key="hourlyRate" align="center" width={150}
                                render={(rate: number) => `$${rate}`} />
                            <Column title="Rating" dataIndex="agentRating" key="agentRating" align="center" width={150}
                                render={(rating: number) => (
                                    <Tag color={rating >= 4 ? "green" : rating >= 3 ? "blue" : rating > 0 ? "orange" : "default"}>
                                        {rating > 0 ? `${rating.toFixed(1)} ⭐` : 'No Rating'}
                                    </Tag>
                                )} />
                            <Column
                                align="center"
                                width={150}
                                title="Action"
                                key="viewAction"
                                render={(_: any, record: any) => (
                                    <Space size="middle">
                                        <Button className="hover:!border-black hover:!text-black" onClick={() => handleChangeAgent(record)}>
                                            Change
                                        </Button>
                                    </Space>
                                )}
                            />
                        </Table>
                        <div>
                            <Pagination
                                currentPage={pageNumber.current}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    </TabPane>
                    <TabPane tab="Attendants with Car" key="2">
                        <Table
                            dataSource={agentListData}
                            className="userlist-table cursor-pointer"
                            bordered
                            pagination={{ pageSize: pageSize, position: ['none'] }}
                            locale={{
                                emptyText: loading ? <Spin /> : (
                                    <div className="ant-empty">
                                        <div className="ant-empty-image">
                                            <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
                                                <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                                                    <ellipse fill="#f5f5f5" cx="32" cy="33" rx="32" ry="7"></ellipse>
                                                    <g fillRule="nonzero" stroke="#d9d9d9">
                                                        <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"></path>
                                                        <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" fill="#fafafa"></path>
                                                    </g>
                                                </g>
                                            </svg>
                                        </div>
                                        <div className="ant-empty-description">There are no attendant available for the date and time selected</div>
                                    </div>
                                )
                            }}
                        >
                            {/* <Column title="ScheduleId" dataIndex="scheduleId" key="scheduleId" align="center" width={200} /> */}
                            <Column title="Attendant Name" dataIndex="agentName" key="agentName" align="center" width={200} />
                            <Column title="Bio" dataIndex="agentBio" key="agentBio" align="center" width={250}
                                render={(bio: string) => bio || 'N/A'} />
                            <Column title="Image" dataIndex="agentImageUrl" key="agentImageUrl" align="center" width={100}
                                render={(imageUrl: string) =>
                                    imageUrl ? (
                                        <img src={imageUrl} alt="agent" className="w-10 h-10 rounded-full mx-auto object-cover" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-full mx-auto bg-gray-200 flex items-center justify-center">
                                            N/A
                                        </div>
                                    )
                                } />
                            <Column title="Phone No" dataIndex="phoneNo" key="phoneNo" align="center" width={150} />
                            <Column title="City" dataIndex="agentCityName" key="agentCityName" align="center" width={150} />
                            <Column title="Hourly Rate" dataIndex="hourlyRate" key="hourlyRate" align="center" width={150}
                                render={(rate: number) => `$${rate}`} />
                            <Column title="Rating" dataIndex="agentRating" key="agentRating" align="center" width={150}
                                render={(rating: number) => (
                                    <Tag color={rating >= 4 ? "green" : rating >= 3 ? "blue" : rating > 0 ? "orange" : "default"}>
                                        {rating > 0 ? `${rating.toFixed(1)} ⭐` : 'No Rating'}
                                    </Tag>
                                )} />
                            <Column
                                align="center"
                                width={150}
                                title="Action"
                                key="viewAction"
                                render={(_: any, record: any) => (
                                    <Space size="middle">
                                        <Button className="hover:!border-black hover:!text-black" onClick={() => handleChangeAgent(record)}>
                                            Change
                                        </Button>
                                    </Space>
                                )}
                            />
                        </Table>
                        <div>
                            <Pagination
                                currentPage={pageNumber.current}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    </TabPane>
                </Tabs>
            </ConfigProvider>

            <Modal
                title={<span className="custom-modal-title">{getModalTitle()}</span>}
                open={isModalVisible}
                onOk={handleSave}
                onCancel={handleCancel}
                closeIcon={<CloseOutlined className="text-gray-500 hover:text-black" />}
                maskClosable={false}
                footer={[
                    <>
                        <Button key="back" onClick={handleCancel} className="hover:!text-black hover:!border-black">
                            Cancel
                        </Button>
                        <Button key="submit" disabled={loading} onClick={handleSave} className="bg-black text-white hover:bg-transparent hover:!text-black hover:!border-black">
                            Confirm
                        </Button>
                    </>
                ]}
            >
                {getModelContent()}
            </Modal>
        </div >
    );
};

export default AvailableAttendants;